/**
 * Hook useTaskPhaseAutomations - Detecta automações vinculadas a fases do Kanban de Tarefas
 *
 * RELEASE: 1.0.0 - 2026-06-01 - Versão inicial. Espelha useConversationPhaseAutomations
 *   consumindo a RPC server-side `get_task_phase_automations(p_category_id, p_tenant_hint)`.
 *   Reusa tipos PhaseAutomation/PhaseAutomationsMap do hook do CRM para manter
 *   compatibilidade com AutomationKanbanColumn e AutomationCard.
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { PhaseAutomation, PhaseAutomationsMap } from '@/hooks/usePhaseAutomations';

// Re-exporta os tipos para consistência
export type { PhaseAutomation, PhaseAutomationsMap };

const logger = createLogger('TaskPhaseAutomations');

/**
 * Hook para detectar automações vinculadas a fases do Kanban de Tarefas
 *
 * @param categoryId - ID da categoria de tarefas (cada categoria é um Kanban)
 */
export function useTaskPhaseAutomations(categoryId: string | null) {
  const { currentTenant } = useAuth();

  // =========================================================================
  // Query — toda a agregação ocorre no servidor
  // =========================================================================
  const { data: automationsByPhase = {}, isLoading } = useQuery({
    queryKey: ['task-phase-automations', categoryId, currentTenant?.id],
    queryFn: async (): Promise<PhaseAutomationsMap> => {
      if (!categoryId || !currentTenant?.id) return {};

      logger.debug(`Buscando automações (server-side) para categoria ${categoryId}`);

      const { data, error } = await (supabase.rpc as any)(
        'get_task_phase_automations',
        {
          p_category_id: categoryId,
          p_tenant_hint: currentTenant.id,
        },
      );

      if (error) {
        logger.error('Erro ao buscar automações via RPC:', error);
        return {};
      }

      const result = ((data as unknown) as PhaseAutomationsMap) || {};

      const totalAutomations = Object.values(result).reduce(
        (sum, arr) => sum + arr.length,
        0,
      );
      logger.debug(
        `RPC retornou ${totalAutomations} automações em ${Object.keys(result).length} fases`,
      );

      return result;
    },
    enabled: !!categoryId && !!currentTenant?.id,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // =========================================================================
  // Helpers memoizados (mesma assinatura dos outros hooks)
  // =========================================================================

  /** Verifica se uma fase tem automações configuradas */
  const hasAutomation = useCallback((phaseId: string): boolean => {
    return (automationsByPhase[phaseId]?.length || 0) > 0;
  }, [automationsByPhase]);

  /** Conta automações de uma fase */
  const automationCount = useCallback((phaseId: string): number => {
    return automationsByPhase[phaseId]?.length || 0;
  }, [automationsByPhase]);

  /** Set de phase_ids que têm automação */
  const phasesWithAutomation = useMemo(() => {
    return new Set(Object.keys(automationsByPhase));
  }, [automationsByPhase]);

  /** Total de automações na categoria */
  const totalAutomations = useMemo(() => {
    return Object.values(automationsByPhase).reduce((sum, arr) => sum + arr.length, 0);
  }, [automationsByPhase]);

  /** Retorna apenas automações do tipo "trigger" (disparam quando entra na fase) */
  const getTriggerAutomations = useCallback((phaseId: string): PhaseAutomation[] => {
    return (automationsByPhase[phaseId] || []).filter(a => a.category === 'trigger');
  }, [automationsByPhase]);

  /** Retorna apenas automações do tipo "action" (movem PARA a fase) */
  const getActionAutomations = useCallback((phaseId: string): PhaseAutomation[] => {
    return (automationsByPhase[phaseId] || []).filter(a => a.category === 'action');
  }, [automationsByPhase]);

  return {
    automationsByPhase,
    hasAutomation,
    automationCount,
    phasesWithAutomation,
    totalAutomations,
    isLoading,
    getTriggerAutomations,
    getActionAutomations,
  };
}
